import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowLeft, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePageSEO } from "@/hooks/use-seo";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";

const SubscriptionSuccess: React.FC = () => {
  usePageSEO("Subscription Successful – Zoocertica", "Your subscription has been activated successfully.", "/subscription-success");
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'retrying'>('verifying');
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  const verifySubscription = useCallback(async () => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage("No subscription session found in URL.");
      return;
    }

    if (!session?.access_token) {
      setStatus('error');
      setErrorMessage("Please log in to verify your subscription.");
      return;
    }

    try {
      setStatus(retryCount > 0 ? 'retrying' : 'verifying');
      
      // Call the verify-subscription edge function
      const { data, error } = await supabase.functions.invoke('verify-subscription', {
        body: { sessionId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Verification error:", error);
        throw new Error(error.message || "Failed to verify subscription");
      }

      if (!data?.ok) {
        throw new Error(data?.error || "Verification failed");
      }

      // Success!
      setSubscriptionDetails(data);
      setStatus('success');
      
      // Invalidate subscription queries to refresh status
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      
      toast({
        title: "Subscription Activated!",
        description: data.isInTrial 
          ? `Your ${data.trialDaysRemaining}-day free trial has started. Welcome to ZooCertica!`
          : "Your subscription is active. Welcome to ZooCertica!",
      });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 3000);

    } catch (error: any) {
      console.error("Subscription verification error:", error);
      setErrorMessage(error.message || "Unable to verify subscription status.");
      
      // If first attempt fails, auto-retry once after 3 seconds
      if (retryCount === 0) {
        setRetryCount(1);
        setTimeout(() => {
          verifySubscription();
        }, 3000);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, session?.access_token, retryCount, queryClient, toast, navigate]);

  useEffect(() => {
    verifySubscription();
  }, [verifySubscription]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    verifySubscription();
  };

  if (status === 'verifying' || status === 'retrying') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
            <h2 className="text-lg font-semibold">
              {status === 'retrying' ? 'Retrying...' : 'Verifying Subscription...'}
            </h2>
            <p className="text-muted-foreground text-center mt-2">
              {status === 'retrying' 
                ? 'Taking a bit longer than expected. Please wait...'
                : 'Please wait while we confirm your subscription.'
              }
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-yellow-600 dark:text-yellow-500" />
            </div>
            <CardTitle className="text-xl">Verification In Progress</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {errorMessage || "We're still processing your subscription. This can take a few moments."}
            </p>
            
            <div className="bg-muted p-4 rounded-lg text-left">
              <h3 className="font-semibold mb-2">What to do:</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Wait a moment and try again</li>
                <li>• Check your email for a confirmation from Stripe</li>
                <li>• If the issue persists, contact support@zoocertica.com</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button onClick={handleRetry} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button variant="outline" asChild>
                <Link to="/admin/billing">
                  Go to Billing
                </Link>
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Return to Home
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-500" />
          </div>
          <CardTitle className="text-2xl text-green-700 dark:text-green-500">
            {subscriptionDetails?.isInTrial ? 'Trial Started!' : 'Subscription Activated!'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            {subscriptionDetails?.isInTrial 
              ? `Your ${subscriptionDetails?.trialDaysRemaining || 14}-day free trial is active. Enjoy full access to all features!`
              : "Welcome to ZooCertica! Your subscription is active and you have full access."
            }
          </p>
          
          {subscriptionDetails && (
            <div className="bg-muted p-4 rounded-lg text-left">
              <h3 className="font-semibold mb-2">Subscription Details</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Plan: {subscriptionDetails.planName}</li>
                <li>• Billing: {subscriptionDetails.billingCycle === 'yearly' ? 'Annual' : 'Monthly'}</li>
                {subscriptionDetails.isInTrial && subscriptionDetails.trialEnd && (
                  <li>• Trial ends: {new Date(subscriptionDetails.trialEnd).toLocaleDateString()}</li>
                )}
              </ul>
            </div>
          )}
          
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-semibold mb-2">What's Next?</h3>
            <ul className="text-sm text-muted-foreground text-left space-y-1">
              <li>• Start managing your facilities and animals</li>
              <li>• Upload permits and veterinary records</li>
              <li>• Access the formulary and legal lookup tools</li>
              <li>• Invite team members to collaborate</li>
            </ul>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Redirecting to dashboard in a few seconds...
          </p>
          
          <div className="flex flex-col gap-2">
            <Button asChild>
              <Link to="/admin">
                Go to Dashboard
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
